import { useEffect, useState } from "react";
import { galleryImages } from "@/mocks/festival";

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = () => setActiveIndex(null);
  const goPrev = () =>
    setActiveIndex((v) => (v === null ? null : (v + galleryImages.length - 1) % galleryImages.length));
  const goNext = () =>
    setActiveIndex((v) => (v === null ? null : (v + 1) % galleryImages.length));

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [activeIndex]);

  return (
    <section id="gallery" className="w-full bg-background-100 px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-6xl mx-auto">
        <div className="reveal mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-xs tracking-[0.24em] text-primary-600">FESTIVAL GALLERY</span>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
              지난 축제의 즐거웠던 순간들
            </h2>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
              작년 축제 현장을 담은 사진들을 모아보았습니다.
              무대와 품평회, 수확체험과 먹거리존까지. 올해도 이보다 더 풍성한 축제가 준비되어 있습니다.
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-secondary-100 px-4 py-2 text-xs md:text-sm text-secondary-900 whitespace-nowrap">
            제26회 현장 스케치
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {galleryImages.map((image, i) => (
            <button
              key={image.title}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`reveal reveal-delay-${(i % 4) + 1} group relative cursor-pointer overflow-hidden rounded-lg bg-background-200 ${
                i === 0 ? "col-span-2 row-span-2 aspect-[4/3] lg:aspect-auto" : "aspect-[4/3]"
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                width={800}
                height={600}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-left translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <span className="inline-block rounded-full bg-accent-500 px-2.5 py-0.5 text-[10px] md:text-[11px] font-medium text-white">
                  {image.category}
                </span>
                <strong className="mt-2 block font-heading text-sm md:text-base text-white">{image.title}</strong>
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/85 backdrop-blur-sm p-4 md:p-8"
          onClick={close}
        >
          <button
            type="button"
            aria-label="갤러리 닫기"
            onClick={close}
            className="absolute right-4 top-4 md:right-6 md:top-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>

          <button
            type="button"
            aria-label="이전 사진"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <button
            type="button"
            aria-label="다음 사진"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white text-2xl hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>

          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="w-full aspect-[4/3] overflow-hidden rounded-lg">
              <img
                src={galleryImages[activeIndex].src}
                alt={galleryImages[activeIndex].alt}
                width={800}
                height={600}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <figcaption className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <span className="inline-block rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-white">
                  {galleryImages[activeIndex].category}
                </span>
                <strong className="ml-3 font-heading text-lg text-white">
                  {galleryImages[activeIndex].title}
                </strong>
              </div>
              <span className="text-xs text-white/60 tabular-nums">
                {activeIndex + 1} / {galleryImages.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}